import { downloadBridge, notificationsActions } from '@repo/core'
import { useEffect } from 'react'
import { Platform } from 'react-native'
import ReactNativeBlobUtil from 'react-native-blob-util'
import { useDispatch } from 'react-redux'
import { blobToBase64 } from './utils'

const isWeb = Platform.OS === 'web'
const isAndroid = Platform.OS === 'android'

/**
 * Mount ONCE at the page/screen root that contains download buttons.
 * Subscribes to the downloadBridge so the saga's emitted blob is handled
 * with the correct platform trigger (web anchor / ReactNativeBlobUtil).
 */
export const useFileDownload = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    return downloadBridge.subscribe(async ({ blob, filename, mimeType }) => {
      if (isWeb) {
        triggerBlobDownloadWeb(blob, filename)

        return
      }

      try {
        await triggerBlobDownloadMobile(blob, filename, mimeType)
      } catch {
        dispatch(
          notificationsActions.addNotification({
            code: 'GENERIC_DOWNLOAD_DOCUMENT_ERROR',
            title: 'common:notifications.errors.GENERIC_DOWNLOAD_DOCUMENT_ERROR.main',
            type: 'error',
            level: 'toast',
            actionTypeRequest: '',
          }),
        )
      }
    })
  }, [dispatch])
}

function triggerBlobDownloadWeb(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

async function triggerBlobDownloadMobile(
  blob: Blob,
  filename: string,
  mimeType: string,
): Promise<void> {
  const base64 = await blobToBase64(blob, mimeType)
  await saveBase64FileAndOpen(base64, filename, mimeType)
}

export async function saveBase64FileAndOpen(
  base64: string,
  filename: string,
  mimeType: string,
): Promise<void> {
  const path = `${ReactNativeBlobUtil.fs.dirs.CacheDir}/${filename}`
  const commaIndex = base64.indexOf(',')
  const data = commaIndex === -1 ? base64 : base64.slice(commaIndex + 1)
  await ReactNativeBlobUtil.fs.writeFile(path, data, 'base64')

  if (isAndroid) {
    await ReactNativeBlobUtil.android.actionViewIntent(path, mimeType)
  } else {
    ReactNativeBlobUtil.ios.previewDocument(path)
  }
}
